import axios from 'axios';

/**
 * 自前ログイン画面が呼ぶ /api/auth/*（docs/features/custom-login/README.md §4）。
 * /callback・/token/refresh と同じく素の axios で呼ぶ（自動生成クライアントには入れない）。
 */

const API_ENDPOINT_URL = process.env.REACT_APP_API_ENDPOINT ?? '';
const ID_TOKEN_KEY = 'CognitoIdToken';

/** API の失敗応答 { message, code }。code の無い応答は HTTP ステータスから決める */
export class AuthApiError extends Error {
  constructor(public readonly code: string, public readonly status?: number) {
    super(code);
    this.name = 'AuthApiError';
  }
}

type ErrorLike = { response?: { status?: number; data?: { code?: unknown } } };

export const toAuthApiError = (err: unknown): AuthApiError => {
  if (err instanceof AuthApiError) {
    return err;
  }
  const response = (err as ErrorLike | null | undefined)?.response;
  const code = response?.data?.code;
  if (typeof code === 'string' && code !== '') {
    return new AuthApiError(code, response?.status);
  }
  if (response?.status === 429) {
    // Laravel の throttle は code を付けずに 429 を返す
    return new AuthApiError('too_many_attempts', 429);
  }
  return new AuthApiError('unknown', response?.status);
};

const post = async <T>(path: string, body: Record<string, string> = {}): Promise<T> => {
  try {
    const { data } = await axios.post<T>(`${API_ENDPOINT_URL}/auth${path}`, body, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      withCredentials: true,
      // 接続が固まったままだと送信ボタンが無期限に無効のままになる。タイムアウトさせて汎用エラーに落とす
      timeout: 15000,
    });
    return data;
  } catch (err) {
    throw toAuthApiError(err);
  }
};

export type LoginResult = { id_token: string };

export const login = (email: string, password: string) =>
  post<LoginResult>('/login', { email, password });
export const signUp = (email: string, password: string) =>
  post<void>('/sign-up', { email, password });
export const confirmSignUp = (email: string, code: string) =>
  post<void>('/sign-up/confirm', { email, code });
export const resendSignUpCode = (email: string) => post<void>('/sign-up/resend', { email });
export const forgotPassword = (email: string) => post<void>('/forgot-password', { email });
export const resetPassword = (email: string, code: string, password: string) =>
  post<void>('/reset-password', { email, code, password });
export const logout = () => post<void>('/logout');

/** ログイン成功時の id_token を、既存の interceptor / ApiConfig が読むキーに保存する */
export const saveIdToken = (idToken: string): void => {
  window.localStorage.setItem(ID_TOKEN_KEY, idToken);
};
